import React, { useState, useEffect } from 'react';
import { getDashboardData, getDashboardMonths, exportDashboard } from '../api/api';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
    Box, Typography, Paper, Grid, FormControl, InputLabel, Select, MenuItem,
    Button, CircularProgress, Alert, Card, CardContent, Tabs, Tab, Chip, Stack,
    useTheme, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip as MuiTooltip, Container, alpha, TextField
} from '@mui/material';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement
} from 'chart.js';
import {
    FaChartBar, FaMoneyBillWave, FaHospital, FaFilter, FaArrowLeft, FaBoxOpen,
    FaCalendarAlt, FaSort, FaSortUp, FaSortDown, FaFileExport
} from 'react-icons/fa';
import { useCompetence } from '../contexts/CompetenceContext';

// Registrar componentes do Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// Componente de Error Boundary
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ error, errorInfo });
        console.error("Uncaught error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <Box sx={{ p: 3, bgcolor: '#fdeded', color: '#5f2120' }}>
                    <Typography variant="h5" gutterBottom>Algo deu errado.</Typography>
                    <details style={{ whiteSpace: 'pre-wrap' }}>
                        {this.state.error && this.state.error.toString()}
                        <br />
                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </details>
                </Box>
            );
        }

        return this.props.children;
    }
}

const DashboardPageContent = () => {
    const theme = useTheme();
    const { globalCompetence, availableMonths: globalMonths } = useCompetence();

    // Estados de Dados
    const [data, setData] = useState(null);
    const [availableMonths, setAvailableMonths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estados de Filtro e Seleção
    const [filterMode, setFilterMode] = useState('single'); // 'single' ou 'period'
    const [selectedMonth, setSelectedMonth] = useState('');
    const [startMonth, setStartMonth] = useState('');
    const [endMonth, setEndMonth] = useState('');
    const [activeTab, setActiveTab] = useState(0); // 0: Overview

    const [selectedCenter, setSelectedCenter] = useState('');
    const [selectedItem, setSelectedItem] = useState(null); // Para drilldown de item

    // Estados de Ordenação da Tabela
    const [sortConfig, setSortConfig] = useState({ key: 'Lucro', direction: 'ascending' });

    // Estados de Gráficos
    const [overviewChartData, setOverviewChartData] = useState(null);
    const [compositionChartData, setCompositionChartData] = useState(null);

    // Sync with global competence (STRICT MODE enforced)
    useEffect(() => {
        if (filterMode === 'single') {
            setSelectedMonth(globalCompetence);
        }
    }, [globalCompetence, filterMode]);

    // Use global months if available
    useEffect(() => {
        if (globalMonths && globalMonths.length > 0) {
            setAvailableMonths(globalMonths);
        }
    }, [globalMonths]);

    const formatCurrency = (value) => {
        if (value === undefined || value === null || isNaN(value)) return "R$ 0,00";
        return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    // Função de Exportação
    const handleExport = async () => {
        try {
            const params = {};
            if (filterMode === 'single' && selectedMonth) {
                params.mes = selectedMonth;
            } else if (filterMode === 'period' && startMonth && endMonth) {
                params.start_month = startMonth;
                params.end_month = endMonth;
            } else {
                alert("Selecione um mês ou período para exportar.");
                return;
            }

            const response = await exportDashboard(params);

            // Create blob and download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `Dashboard_${params.mes || 'Periodo'}.xlsx`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Erro ao exportar:", error);
            alert("Erro ao exportar dados. Tente novamente.");
        }
    };

    // Preparar Gráficos da Visão Geral
    const prepareOverviewCharts = (dashboardData) => {
        if (!dashboardData || !dashboardData.custos_por_centro) return;

        const centros = Object.keys(dashboardData.custos_por_centro);
        const custos = Object.values(dashboardData.custos_por_centro);
        const lucros = centros.map(c => dashboardData.relatorio_analitico[c]?.Lucro || 0);

        // Top 10 Centros por Custo
        const sortedIndices = custos.map((c, i) => ({ c, i })).sort((a, b) => b.c - a.c).slice(0, 10);
        const topCentros = sortedIndices.map(item => centros[item.i]);
        const topCustos = sortedIndices.map(item => custos[item.i]);

        // Top 5 Contas de Custo/Despesa (Novo KPI)
        const contasMap = {};
        const composicao = dashboardData.composicao_contas || {};

        Object.values(composicao).forEach(itens => {
            itens.forEach(item => {
                // Filtrar apenas Custo e Despesa
                if (['Custo', 'Despesa'].includes(item.tipo)) {
                    contasMap[item.conta] = (contasMap[item.conta] || 0) + (item.valor || 0);
                }
            });
        });

        const sortedContas = Object.entries(contasMap)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5);

        const topContasLabels = sortedContas.map(([conta]) => conta);
        const topContasValores = sortedContas.map(([, valor]) => valor);

        setOverviewChartData({
            lucroPrejuizo: {
                labels: centros,
                datasets: [{
                    label: 'Lucro/Prejuízo (R$)',
                    data: lucros,
                    backgroundColor: lucros.map(l => l >= 0 ? alpha(theme.palette.success.main, 0.7) : alpha(theme.palette.error.main, 0.7)),
                    borderColor: lucros.map(l => l >= 0 ? theme.palette.success.main : theme.palette.error.main),
                    borderWidth: 1
                }]
            },
            topCustos: {
                labels: topCentros,
                datasets: [{
                    label: 'Custo Total (R$)',
                    data: topCustos,
                    backgroundColor: alpha(theme.palette.primary.main, 0.7),
                    borderColor: theme.palette.primary.main,
                    borderWidth: 1
                }]
            },
            topContas: {
                labels: topContasLabels,
                datasets: [{
                    label: 'Valor (R$)',
                    data: topContasValores,
                    backgroundColor: [
                        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'
                    ],
                    borderWidth: 1
                }]
            }
        });
    };

    // ... (fetchDashboardData and other functions remain same)

    // ...

    {/* Gráficos */ }
    <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" gutterBottom>Lucro/Prejuízo por Centro</Typography>
                <Box sx={{ height: 320 }}>
                    {overviewChartData && (
                        <Bar
                            data={overviewChartData.lucroPrejuizo}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { position: 'top' },
                                    tooltip: {
                                        callbacks: {
                                            label: function (context) {
                                                return formatCurrency(context.raw);
                                            }
                                        }
                                    }
                                }
                            }}
                        />
                    )}
                </Box>
            </Paper>
        </Grid>
        <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" gutterBottom>Top 10 Centros de Custo</Typography>
                <Box sx={{ height: 320 }}>
                    {overviewChartData && (
                        <Bar
                            data={overviewChartData.topCustos}
                            options={{
                                indexAxis: 'y',
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { display: false },
                                    tooltip: {
                                        callbacks: {
                                            label: function (context) {
                                                return formatCurrency(context.raw);
                                            }
                                        }
                                    }
                                }
                            }}
                        />
                    )}
                </Box>
            </Paper>
        </Grid>

        {/* Nova Linha para Top Contas */}
        <Grid item xs={12} lg={12}>
            <Paper sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" gutterBottom>Top 5 Contas de Maior Impacto (Custos e Despesas)</Typography>
                <Box sx={{ height: 320 }}>
                    {overviewChartData && overviewChartData.topContas && (
                        <Bar
                            data={overviewChartData.topContas}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { display: false },
                                    tooltip: {
                                        callbacks: {
                                            label: function (context) {
                                                return formatCurrency(context.raw);
                                            }
                                        }
                                    }
                                }
                            }}
                        />
                    )}
                </Box>
            </Paper>
        </Grid>
    </Grid>

    // Carregar Dados do Dashboard
    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        setSelectedItem(null);

        try {
            let res;
            if (filterMode === 'period' && startMonth && endMonth) {
                res = await getDashboardData(null, startMonth, endMonth);
            } else {
                res = await getDashboardData(selectedMonth);
            }

            if (res.status === 204 || !res.data || (typeof res.data === 'object' && Object.keys(res.data).length === 0)) {
                setData(null);
            } else {
                setData(res.data);
                prepareOverviewCharts(res.data);
            }
        } catch (err) {
            console.error("fetchDashboardData: Erro:", err);
            if (err.response && err.response.status === 404) {
                setData(null);
            } else {
                setError(err.message || "Erro ao carregar dados.");
            }
        } finally {
            setLoading(false);
        }
    };

    // Carregar Meses Disponíveis
    useEffect(() => {
        const fetchMonths = async () => {
            try {
                const res = await getDashboardMonths();
                setAvailableMonths(res.data);
                fetchDashboardData();
            } catch (err) {
                console.error("Erro ao carregar meses:", err);
            }
        };
        fetchMonths();
    }, [selectedMonth, filterMode]);

    // Função para aplicar filtro de período manualmente
    const handleApplyPeriodFilter = () => {
        if (startMonth && endMonth) {
            if (startMonth > endMonth) {
                alert("O mês inicial não pode ser maior que o mês final.");
                return;
            }
            fetchDashboardData();
        }
    };

    // Preparar Gráfico de Composição (Detalhe CC)
    useEffect(() => {
        if (!data || !selectedCenter) return;

        const ccData = data.custo_unitario_detalhado?.[selectedCenter];
        if (!ccData) return;

        const producoes = ccData.producoes || {};
        const itensLabels = Object.keys(producoes);
        const itensCustos = Object.values(producoes).map(p => p.custo_total_item);

        setCompositionChartData({
            itens: {
                labels: itensLabels,
                datasets: [{
                    data: itensCustos,
                    backgroundColor: [
                        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
                        '#C9CBCF', '#7BC225', '#E63946', '#F1FAEE'
                    ],
                    hoverOffset: 4
                }]
            }
        });

    }, [data, selectedCenter]);

    // Lógica de Ordenação da Tabela
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortedCentros = () => {
        if (!data || !data.custos_por_centro) return [];

        const centros = Object.keys(data.custos_por_centro);

        return centros.sort((a, b) => {
            const analiticoA = data.relatorio_analitico[a] || {};
            const analiticoB = data.relatorio_analitico[b] || {};

            let valA, valB;

            switch (sortConfig.key) {
                case 'Centro': valA = a; valB = b; break;
                case 'Receita': valA = analiticoA.Receita || 0; valB = analiticoB.Receita || 0; break;
                case 'Custo': valA = analiticoA['Custo Total'] || analiticoA['Custo_Total'] || 0; valB = analiticoB['Custo Total'] || analiticoB['Custo_Total'] || 0; break;
                case 'Lucro': valA = analiticoA.Lucro || 0; valB = analiticoB.Lucro || 0; break;
                default: return 0;
            }

            if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
    };

    const SummaryCard = ({ title, value, icon, color }) => (
        <Card sx={{ height: '100%', borderLeft: `6px solid ${color}`, borderRadius: 4 }} className="glassmorphism sach-card">
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                    <Box sx={{ color: color, fontSize: 24 }}>{icon}</Box>
                    <Typography color="textSecondary" variant="subtitle2" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                        {title}
                    </Typography>
                </Stack>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {value}
                </Typography>
            </CardContent>
        </Card>
    );

    // Renderização Condicional
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>Carregando Dashboard...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ m: 4 }}>Erro: {error}</Alert>;
    }

    if (!data) {
        return (
            <Container sx={{ textAlign: 'center', p: 6 }}>
                <Typography variant="h4" gutterBottom>Nenhum dado encontrado</Typography>
                <Typography variant="body1" paragraph>Vá para a página de Importação para processar dados ou ajuste os filtros.</Typography>
                <Button variant="contained" href="/importar" sx={{ mr: 2 }}>Ir para Importação</Button>
                <Button variant="outlined" onClick={() => window.location.reload()}>Recarregar</Button>
            </Container>
        );
    }

    const sortedCentros = getSortedCentros();
    const resumo = data.resumo || {};
    const kpis = data.kpis || {};

    // Componente de Cartão KPI Avançado
    const KPICard = ({ title, value, subtext, trend, trendValue, icon, color, tooltip }) => {
        const isPositive = trend === 'up';
        const trendColor = isPositive ? theme.palette.success.main : theme.palette.error.main;
        const TrendIcon = isPositive ? FaSortUp : FaSortDown;

        return (
            <Card sx={{ height: '100%', borderTop: `6px solid ${color}`, borderRadius: 4 }} className="glassmorphism sach-card">
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography color="textSecondary" variant="subtitle2" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                                {title}
                            </Typography>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'text.primary', my: 1 }}>
                                {value}
                            </Typography>
                        </Box>
                        <Box sx={{ color: color, fontSize: 24, opacity: 0.8 }}>{icon}</Box>
                    </Stack>

                    {trendValue !== undefined && trendValue !== null && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                            <Chip
                                icon={<TrendIcon size={14} />}
                                label={`${trendValue}%`}
                                size="small"
                                sx={{
                                    bgcolor: alpha(trendColor, 0.1),
                                    color: trendColor,
                                    fontWeight: 'bold',
                                    '& .MuiChip-icon': { color: trendColor }
                                }}
                            />
                            <Typography variant="caption" color="textSecondary">
                                {subtext}
                            </Typography>
                        </Stack>
                    )}
                    {tooltip && (
                        <MuiTooltip title={tooltip}>
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, textDecoration: 'underline', cursor: 'help' }}>
                                Saiba mais
                            </Typography>
                        </MuiTooltip>
                    )}
                </CardContent>
            </Card>
        );
    };

    // --- VIEW: DETALHE DO ITEM (DRILLDOWN) ---
    if (selectedItem && selectedCenter) {
        const itemData = data.custo_unitario_detalhado?.[selectedCenter]?.producoes?.[selectedItem];
        if (!itemData) return <Alert severity="warning">Dados detalhados não disponíveis.</Alert>;

        return (
            <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
                <Button startIcon={<FaArrowLeft />} onClick={() => setSelectedItem(null)} sx={{ mb: 3 }}>
                    Voltar para {selectedCenter}
                </Button>

                <Paper sx={{ p: 3, mb: 4, borderLeft: 4, borderColor: 'primary.main', bgcolor: 'background.paper' }}>
                    <Typography variant="h5" gutterBottom fontWeight="bold" color="text.primary">{selectedItem}</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="caption" color="textSecondary">Código</Typography>
                                <Typography variant="body1" fontWeight="bold" color="text.primary">{itemData.codigo}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="caption" color="textSecondary">Quantidade Produzida</Typography>
                                <Typography variant="body1" fontWeight="bold" color="text.primary">{itemData.quantidade}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="caption" color="textSecondary">Custo Unitário</Typography>
                                <Typography variant="body1" fontWeight="bold" color="primary.main">{formatCurrency(itemData.custo_unitario_total)}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="caption" color="textSecondary">Custo Total</Typography>
                                <Typography variant="body1" fontWeight="bold" color="text.primary">{formatCurrency(itemData.custo_total_item)}</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <FaBoxOpen style={{ marginRight: 8 }} /> Composição do Custo
                </Typography>
                <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: 'action.hover' }}>
                            <TableRow>
                                <TableCell>Componente de Custo</TableCell>
                                <TableCell align="right">Valor Total</TableCell>
                                <TableCell align="right">Por Unidade</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {itemData.detalhe_despesas.map((detalhe, idx) => (
                                <TableRow key={idx} hover>
                                    <TableCell>{detalhe.descricao}</TableCell>
                                    <TableCell align="right">{formatCurrency(detalhe.valor_total_cc)}</TableCell>
                                    <TableCell align="right">{formatCurrency(detalhe.custo_unitario_despesa)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }

    // --- VIEW: DETALHE DO CENTRO DE CUSTO ---
    if (selectedCenter) {
        const ccAnalitico = data.relatorio_analitico[selectedCenter] || {};

        return (
            <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
                <Button startIcon={<FaArrowLeft />} onClick={() => setSelectedCenter('')} sx={{ mb: 3 }}>
                    Voltar para Visão Geral
                </Button>

                <Paper sx={{ p: 3, mb: 4, borderTop: 4, borderColor: 'primary.main', bgcolor: 'background.paper' }}>
                    <Typography variant="h4" gutterBottom fontWeight="bold" color="text.primary">{selectedCenter}</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2, textAlign: 'center' }}>
                                <Typography variant="subtitle2" color="success.main" fontWeight="bold">RECEITA TOTAL</Typography>
                                <Typography variant="h4" fontWeight="bold" color="success.dark">{formatCurrency(ccAnalitico.Receita)}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 2, textAlign: 'center' }}>
                                <Typography variant="subtitle2" color="error.main" fontWeight="bold">CUSTO TOTAL</Typography>
                                <Typography variant="h4" fontWeight="bold" color="error.dark">{formatCurrency(ccAnalitico['Custo Total'] || ccAnalitico.Custo_Total || 0)}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2, textAlign: 'center', height: '100%' }}>
                                <Typography variant="subtitle2" color="primary.main" fontWeight="bold">DISTRIBUIÇÃO</Typography>
                                <Box sx={{ height: 150, mt: 2 }}>
                                    {compositionChartData && <Doughnut data={compositionChartData.itens} options={{ maintainAspectRatio: false }} />}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper sx={{ overflow: 'hidden', bgcolor: 'background.paper' }}>
                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h6" color="text.primary">Composição Detalhada de Contas</Typography>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: 'action.hover' }}>
                                <TableRow>
                                    <TableCell>Conta / Origem</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell align="right">Valor</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.composicao_contas?.[selectedCenter]?.map((item, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">{item.descricao}</Typography>
                                            {item.detalhe_regra && <Typography variant="caption" color="textSecondary">{item.detalhe_regra}</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={item.origem_tipo === 'Direto' ? item.tipo : 'Rateio Recebido'}
                                                size="small"
                                                color={item.origem_tipo === 'Direto' ? (item.tipo === 'Receita' ? 'success' : 'default') : 'primary'}
                                                variant={item.origem_tipo === 'Direto' ? 'outlined' : 'filled'}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                            {formatCurrency(item.valor)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!data.composicao_contas?.[selectedCenter] && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">Nenhum dado de composição disponível.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        );
    }

    // --- VIEW: VISÃO GERAL (DEFAULT) ---
    return (
        <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }} className="page-fade-in">
            {/* Header e Filtros */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 2 }}>
                <Box>
                    <Typography variant="h3" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }} className="premium-gradient-text">
                        <FaChartBar /> Dashboard Gerencial
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {data.mes ? `Dados referentes a: ${data.mes}` : 'Análise estratégica de custos e resultados.'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <MuiTooltip title="Exportar Relatório Excel">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FaFileExport />}
                            onClick={handleExport}
                            disabled={!data}
                            sx={{ textTransform: 'none' }}
                        >
                            Exportar Excel
                        </Button>
                    </MuiTooltip>

                    <Paper sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }} className="glassmorphism">
                        <Tabs value={filterMode} onChange={(e, val) => setFilterMode(val)} sx={{ minHeight: 0 }}>
                            <Tab value="single" label="Mês" sx={{ minHeight: 36, py: 0 }} />
                            <Tab value="period" label="Período" sx={{ minHeight: 36, py: 0 }} />
                        </Tabs>

                        {filterMode === 'single' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FaCalendarAlt color="gray" />
                                <Chip
                                    label={selectedMonth || "Nenhuma Selecionada"}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                    (Utilize o menu lateral para alterar)
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    type="month"
                                    value={startMonth}
                                    onChange={(e) => setStartMonth(e.target.value)}
                                    size="small"
                                    sx={{ width: 150 }}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <Typography variant="caption" sx={{ mx: 1 }}>até</Typography>
                                <TextField
                                    type="month"
                                    value={endMonth}
                                    onChange={(e) => setEndMonth(e.target.value)}
                                    size="small"
                                    sx={{ width: 150 }}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <MuiTooltip title="Aplicar Filtro">
                                    <IconButton onClick={handleApplyPeriodFilter} color="primary" size="small">
                                        <FaFilter />
                                    </IconButton>
                                </MuiTooltip>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Box >

            <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Visão Geral" />
            </Tabs>

            {
                activeTab === 0 && (
                    <>
                        {/* Cards KPI Financeiros */}
                        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'text.secondary', fontWeight: 'bold' }}>
                            Resumo Financeiro
                        </Typography>
                        <Grid container spacing={3} mb={4}>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryCard
                                    title="Receita Total"
                                    value={formatCurrency(resumo.total_receita)}
                                    icon={<FaMoneyBillWave />}
                                    color={theme.palette.success.main}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryCard
                                    title="Custos Totais"
                                    value={formatCurrency(resumo.total_custos)}
                                    icon={<FaChartBar />}
                                    color={theme.palette.error.main}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryCard
                                    title="Despesas Totais"
                                    value={formatCurrency(resumo.total_despesas)}
                                    icon={<FaChartBar />}
                                    color={theme.palette.warning.main}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryCard
                                    title="Resultado Líquido"
                                    value={formatCurrency(resumo.lucro_prejuizo)}
                                    icon={<FaHospital />}
                                    color={resumo.lucro_prejuizo >= 0 ? theme.palette.success.dark : theme.palette.error.dark}
                                />
                            </Grid>
                        </Grid>

                        {/* Novos KPIs Operacionais (Quick Wins) */}
                        {kpis && kpis.margem_operacional !== undefined && (
                            <>
                                <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'text.secondary', fontWeight: 'bold' }}>
                                    Indicadores Operacionais
                                </Typography>
                                <Grid container spacing={3} mb={4}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <KPICard
                                            title="Margem Operacional"
                                            value={`${kpis.margem_operacional || 0}%`}
                                            trend={kpis.margem_operacional >= 0 ? 'up' : 'down'}
                                            trendValue={null}
                                            subtext="Sobre Receita Líquida"
                                            icon={<FaChartBar />}
                                            color="#2196F3"
                                            tooltip="Lucro Operacional dividido pela Receita Líquida Total"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <KPICard
                                            title="Custo por Paciente/Dia"
                                            value={formatCurrency(kpis.custo_por_paciente)}
                                            trend={kpis.mom?.custos_pct > 0 ? 'up' : 'down'}
                                            trendValue={kpis.mom?.custos_pct}
                                            subtext="vs Mês Anterior"
                                            icon={<FaHospital />}
                                            color="#E91E63"
                                            tooltip="Custo Total dividido pelo total de diárias/pacientes-dia"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <KPICard
                                            title="Receita por Paciente/Dia"
                                            value={formatCurrency(kpis.receita_por_paciente)}
                                            trend={kpis.mom?.receita_pct > 0 ? 'up' : 'down'}
                                            trendValue={kpis.mom?.receita_pct}
                                            subtext="vs Mês Anterior"
                                            icon={<FaMoneyBillWave />}
                                            color="#4CAF50"
                                            tooltip="Receita Total dividida pelo total de diárias/pacientes-dia"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <KPICard
                                            title="Total Pacientes-Dia"
                                            value={kpis.total_pacientes_dia || 0}
                                            trend={null}
                                            trendValue={null}
                                            subtext="Volume de Atendimentos"
                                            icon={<FaBoxOpen />}
                                            color="#FF9800"
                                            tooltip="Soma das quantidades de produção classificadas como 'Diária' ou 'Paciente'"
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        {/* Gráficos */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} lg={6}>
                                <Paper sx={{ p: 3, height: 400 }}>
                                    <Typography variant="h6" gutterBottom>Lucro/Prejuízo por Centro</Typography>
                                    <Box sx={{ height: 320 }}>
                                        {overviewChartData && <Bar data={overviewChartData.lucroPrejuizo} options={{ responsive: true, maintainAspectRatio: false }} />}
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <Paper sx={{ p: 3, height: 400 }}>
                                    <Typography variant="h6" gutterBottom>Top 10 Maiores Custos</Typography>
                                    <Box sx={{ height: 320 }}>
                                        {overviewChartData && <Bar data={overviewChartData.topCustos} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y' }} />}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Tabela Principal */}
                        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                            <TableContainer>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell onClick={() => requestSort('Centro')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                                <Box display="flex" alignItems="center">
                                                    Centro de Custo
                                                    {sortConfig.key === 'Centro' && (sortConfig.direction === 'ascending' ? <FaSortUp style={{ marginLeft: 4 }} /> : <FaSortDown style={{ marginLeft: 4 }} />)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right" onClick={() => requestSort('Receita')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                                <Box display="flex" alignItems="center" justifyContent="flex-end">
                                                    Receita
                                                    {sortConfig.key === 'Receita' && (sortConfig.direction === 'ascending' ? <FaSortUp style={{ marginLeft: 4 }} /> : <FaSortDown style={{ marginLeft: 4 }} />)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right" onClick={() => requestSort('Custo')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                                <Box display="flex" alignItems="center" justifyContent="flex-end">
                                                    Custo Total
                                                    {sortConfig.key === 'Custo' && (sortConfig.direction === 'ascending' ? <FaSortUp style={{ marginLeft: 4 }} /> : <FaSortDown style={{ marginLeft: 4 }} />)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right" onClick={() => requestSort('Lucro')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                                <Box display="flex" alignItems="center" justifyContent="flex-end">
                                                    Resultado
                                                    {sortConfig.key === 'Lucro' && (sortConfig.direction === 'ascending' ? <FaSortUp style={{ marginLeft: 4 }} /> : <FaSortDown style={{ marginLeft: 4 }} />)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" fontWeight="bold">Ação</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedCentros.map((centro) => {
                                            const analitico = data.relatorio_analitico[centro] || {};
                                            // Handle both 'Custo Total' and 'Custo_Total' depending on backend version
                                            const custoTotal = analitico['Custo Total'] !== undefined ? analitico['Custo Total'] : (analitico.Custo_Total || 0);
                                            const isZero = analitico.Receita === 0 && custoTotal === 0;

                                            return (
                                                <TableRow
                                                    key={centro}
                                                    hover
                                                    onClick={() => setSelectedCenter(centro)}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                                                        {centro}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {analitico.Receita === 0 ? '-' : formatCurrency(analitico.Receita)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {custoTotal === 0 ? '-' : formatCurrency(custoTotal)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {isZero ? '-' : (
                                                            <Chip
                                                                label={formatCurrency(analitico.Lucro)}
                                                                color={analitico.Lucro >= 0 ? 'success' : 'error'}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Button size="small" variant="text">Detalhes</Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box sx={{ p: 2, textAlign: 'right', bgcolor: 'action.hover' }}>
                                <Typography variant="caption" color="textSecondary">
                                    Mostrando {sortedCentros.length} centros de custo
                                </Typography>
                            </Box>
                        </Paper>
                    </>
                )
            }
        </Box >
    );
};

const DashboardPage = () => {
    return (
        <ErrorBoundary>
            <DashboardPageContent />
        </ErrorBoundary>
    );
};

export default DashboardPage;

